import type {LogCardData, TimelineDayGroup} from "~/modules/activityTimeline/types/activityTimeline.types";
import {formatDateFromJsDate} from "~/utilities/utilities";

function startOfDay(date: Date): Date {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
}

function formatDayKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function formatGroupLabel(date: Date, today: Date, yesterday: Date): string {
    const dateKey = formatDayKey(date);
    if (dateKey === formatDayKey(today)) {
        return "Today";
    }
    if (dateKey === formatDayKey(yesterday)) {
        return "Yesterday";
    }
    return formatDateFromJsDate(date);
}

export function timelineGenerator(
    items: Array<LogCardData>,
    maxItems?: number,
): Array<TimelineDayGroup> {
    const sorted = [...items].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    const limitedItems =
        maxItems !== undefined ? sorted.slice(0, maxItems) : sorted;

    const today = startOfDay(new Date());
    const yesterday = startOfDay(new Date(today));
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = new Map<string, TimelineDayGroup>();

    for (const item of limitedItems) {
        const date = new Date(item.timestamp);
        const dateKey = formatDayKey(date);
        const label = formatGroupLabel(date, today, yesterday);
        const existing = groups.get(dateKey);
        if (existing === undefined) {
            groups.set(dateKey, {
                label,
                dateKey,
                items: [item],
            });
        } else {
            existing.items.push(item);
        }
    }

    return [...groups.values()];
}

export function limitTimelineDayGroups(
    groups: Array<TimelineDayGroup>,
    maxItems: number,
): Array<TimelineDayGroup> {
    if (maxItems <= 0) {
        return [];
    }

    let remaining = maxItems;
    const limitedGroups: Array<TimelineDayGroup> = [];

    for (const group of groups) {
        if (remaining <= 0) {
            break;
        }

        if (group.items.length <= remaining) {
            limitedGroups.push(group);
            remaining -= group.items.length;
            continue;
        }

        limitedGroups.push({
            label: group.label,
            dateKey: group.dateKey,
            items: group.items.slice(0, remaining),
        });
        break;
    }

    return limitedGroups;
}
